import { Box, MenuItem, Select, Skeleton, Stack, Tooltip } from '@mui/material';
import { QuestionIcon } from '@phosphor-icons/react';
import { useQuery } from '@tanstack/react-query';
import {
  CommunicationVendor,
  PhoneNumberCapabilityChannel,
  PhoneNumberCapabilityDirection,
} from 'primary-schema';
import { useId } from 'react';

import { Alert, ExternalLink, FormField, api, getQueryProps } from 'common';

import { formatPhoneNumber } from '../../../../../../../utils/string';
import { useFlowDefinitionContext } from '../../../../context';
import { useReactFlowViewContext } from '../../../context/context';
import { useSelectedNodeField } from '../../ConfigFields/useSelectedNodeField';

export function AutomatedVoiceCallerPhoneNumberFormField() {
  const { flowDefinition } = useFlowDefinitionContext();
  const { organizationId } = flowDefinition;
  const { isEditable } = useReactFlowViewContext();

  const { value, setValue, visibleError } = useSelectedNodeField<string | undefined>(
    'config.callerPhoneNumberId'
  );
  const fieldId = useId();
  const {
    data: phoneNumberConfigurations,
    isPending: isPhoneNumberConfigurationsPending,
    isError,
    error,
  } = useQuery({
    ...getQueryProps(api.client.AssistantPhoneNumberConfiguration, 'findAll', {
      organizationIds: [organizationId],
    }),
    // Automated Voice dials through Twilio only, so it needs no Vapi capability.
    select: (data) => {
      return data.filter((phoneNumberConfiguration) =>
        phoneNumberConfiguration.capabilities.some(
          (capability) =>
            capability.channel === PhoneNumberCapabilityChannel.Voice &&
            capability.direction === PhoneNumberCapabilityDirection.Outbound &&
            capability.vendor === CommunicationVendor.Twilio
        )
      );
    },
  });

  if (isPhoneNumberConfigurationsPending) {
    return <Skeleton variant="rectangular" height={40} />;
  }

  if (isError) {
    return <Alert error={error} title="Failed to load phone number configurations" />;
  }

  return (
    <Stack spacing={0.5}>
      <FormField
        id={fieldId}
        label="Caller Phone Number"
        labelAdornment={
          <Tooltip placement="top" arrow title="Phone number to call from.">
            <Box>
              <QuestionIcon />
            </Box>
          </Tooltip>
        }
        error={visibleError}
      >
        <Select
          id={fieldId}
          aria-label="Caller Phone Number"
          disabled={!isEditable}
          value={value ?? ''}
          displayEmpty
          onChange={(event) => {
            setValue(event.target.value);
          }}
        >
          <MenuItem value="" disabled>
            <em>Select a phone number</em>
          </MenuItem>
          {phoneNumberConfigurations.map((phoneNumberConfiguration) => (
            <MenuItem key={phoneNumberConfiguration.id} value={phoneNumberConfiguration.id}>
              {phoneNumberConfiguration.displayName}
              {` [${formatPhoneNumber(phoneNumberConfiguration.phoneNumber)}]`}
            </MenuItem>
          ))}
        </Select>
      </FormField>
      <ExternalLink to="/phone-numbers" sx={{ alignSelf: 'flex-start', typography: 'caption' }}>
        Manage phone numbers
      </ExternalLink>
    </Stack>
  );
}
